import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ClearIcon from "@mui/icons-material/Clear";

import AuthContext from "../store/AuthContext";
import useCommonContext from "../store/CommonContext";
import ApiEndpoints from "../network/ApiEndPoints";
import { get, getAxios, postJsonData } from "../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";

import ChangePass from "../modals/ChangePass";
import UploadImageModal from "../modals/UploadImageModal";
import ViewModal from "../modals/ViewModal";

const NewMyProfile = () => {
  const authCtx = useContext(AuthContext);
  const { refreshUser } = useCommonContext();

  const user = authCtx?.user;
  const access = authCtx?.token;

  const [editable, setEditable] = useState("");
  const [progress, setProgress] = useState(false);

  const profiledata =
    user.role === "Admin"
      ? user.admin_profile
      : user.role === "Merchant"
      ? user.merchant_profile
      : user.developer_profile;

  const [formData, setFormData] = useState({
    address: profiledata?.address || "",
    account_no: profiledata?.account_no || "",
    ifsc: profiledata?.ifsc || "",
    pan_number: profiledata?.pan_number || "",
    aadhaar_number: profiledata?.aadhaar_number || "",
    emergency_number: profiledata?.emergency_number || "",
    dob: profiledata?.dob || "",
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const getUserAgain = () => {
    get(
      ApiEndpoints.GET_USER_BIGG,
      "",
      setProgress,
      (res) => {
        getAxios(access);
        authCtx.saveUser(res.data.data);
      },
      (err) => apiErrorToast(err)
    );
  };

  const handleFormSubmit = () => {
    postJsonData(
      ApiEndpoints.UPDATE_USER_PROFILE,
      { ...formData, id: user.id },
      setProgress,
      () => {
        okSuccessToast("Profile updated successfully");
        refreshUser();
        getUserAgain();
        setEditable("");
      },
      (err) => apiErrorToast(err)
    );
  };

  useEffect(() => {
    getUserAgain();
  }, []);

  return (
    <Grid container spacing={3} sx={{ mt: 2, px: 1 }}>
      {/* ================= PROFILE HEADER ================= */}
      <Grid item xs={12}>
  <Paper
    elevation={4}
    sx={{
      p: 3,
      borderRadius: 3,
      display: "flex",
      flexDirection: { xs: "column", sm: "row" }, // 👈 mobile me column
      justifyContent: "space-between",
      alignItems: { xs: "flex-start", sm: "center" },
      gap: { xs: 2, sm: 0 },
      background: "linear-gradient(135deg, #1e3c72, #1a1230)",
      color: "white",
    }}
  >
    {/* Left Side */}
    <Box display="flex" alignItems="center" gap={2}>
      <Box position="relative">
        <ViewModal
          row={user}
          title="View Profile Image"
          fileKey="profile_image"
          view="profileImage"
        />
        <Box position="absolute" bottom={-6} right={-6}>
          <UploadImageModal
            endpt={ApiEndpoints.UPLOAD_PROFILE_IMG}
            getUserAgain={getUserAgain}
          />
        </Box>
      </Box>

      <Typography variant="h5" fontWeight="bold">
        {user?.name}
      </Typography>
    </Box>

    {/* Right Side Buttons */}
    <Box
      display="flex"
      gap={2}
      mt={{ xs: 2, sm: 0 }} // 👈 mobile me neeche space
      width={{ xs: "100%", sm: "auto" }}
      justifyContent={{ xs: "space-between", sm: "flex-end" }}
    >
      <Button
        fullWidth
        variant="contained"
        onClick={() =>
          setEditable(editable === "personal" ? "" : "personal")
        }
        sx={{
          background: "#ff9800",
          flex: { xs: 1, sm: "unset" },
        }}
      >
        {editable === "personal" ? <ClearIcon /> : <ModeEditIcon />}
        <Typography sx={{ ml: 1 }}>
          {editable === "personal" ? "Cancel" : "Edit"}
        </Typography>
      </Button>

      <Box flex={{ xs: 1, sm: "unset" }}>
        <ChangePass />
      </Box>
    </Box>
  </Paper>
</Grid>


      {/* ================= PERSONAL INFORMATION ================= */}
      <Grid item xs={12}>
        <Paper elevation={6} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Personal Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="User Id"
                fullWidth
                size="small"
                value={user?.id}
                disabled
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Address"
                id="address"
                fullWidth
                size="small"
                value={formData.address}
                onChange={handleChange}
                disabled={editable !== "personal"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Account No"
                id="account_no"
                fullWidth
                size="small"
                value={formData.account_no}
                onChange={handleChange}
                disabled={editable !== "personal"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="IFSC No"
                id="ifsc"
                fullWidth
                size="small"
                value={formData.ifsc}
                onChange={handleChange}
                disabled={editable !== "personal"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="PAN Number"
                id="pan_number"
                fullWidth
                size="small"
                value={formData.pan_number}
                onChange={handleChange}
                disabled={editable !== "personal"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Aadhar Number"
                id="aadhaar_number"
                fullWidth
                size="small"
                value={formData.aadhaar_number}
                onChange={handleChange}
                disabled={editable !== "personal"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Emergency Number"
                id="emergency_number"
                fullWidth
                size="small"
                value={formData.emergency_number}
                onChange={handleChange}
                disabled={editable !== "personal"}
              />
            </Grid>
          </Grid>

          <Button
            variant="contained"
            sx={{ mt: 3 }}
            disabled={editable !== "personal"}
            onClick={handleFormSubmit}
          >
            Update Information
          </Button>
        </Paper>
      </Grid>

      {/* ================= ACCOUNT INFORMATION ================= */}
      <Grid item xs={12}>
        <Paper elevation={6} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Account Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Email"
                fullWidth
                size="small"
                value={user?.email}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Phone Number"
                fullWidth
                size="small"
                value={user?.username}
                disabled
              />
            </Grid>
          </Grid>

          {/* ✅ MANAGE ACCOUNT BUTTON ADDED BACK */}
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            disabled={editable !== "personal"}
            onClick={handleFormSubmit}
          >
            Manage Account
          </Button>
        </Paper>
      </Grid>

      {/* ================= DEVELOPER SKILLS ================= */}
      {/* {user.role === "Developer" && (
        <Grid item xs={12}>
          <Paper elevation={6} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Developer Skills
            </Typography>

            <Grid container spacing={2}>
              {[
                "React",
                "Next.js",
                "TypeScript",
                "Node.js",
                "MongoDB",
                "TailwindCSS",
              ].map((skill, i) => (
                <Grid item xs={6} sm={3} md={2} key={i} textAlign="center">
                  <Avatar sx={{ mx: "auto", mb: 1 }}>{skill[0]}</Avatar>
                  <Typography variant="body2" fontWeight="bold">
                    {skill}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      )} */}
    </Grid>
  );
};
 
export default NewMyProfile;
